import cn from "classnames"

import { Button, Divider, Flex, Select, Text } from "@/atoms"
import { useTranslation } from "@/hooks/useTranslation"
import { CloseOutlined } from "@/icons"
import { Form } from "@/organisms/Form"
import {
  getMcpToolNamesOption,
  getWholeWorkloadAccessTypesOption,
  hasWholeWorkloadConditionToAdd,
  isOfferedInCard,
  isToolAccessPart,
  isToolAccessRow,
  isToolAccessWithoutAccess,
  isWholeWorkloadRow,
  isWholeWorkloadWithoutDestinations,
  TOOL_ACCESS_RULE,
  toolAccessRow,
  wholeWorkloadRules
} from "@/pages/GovernancePage/applications/ai-policies/liveRuleGroups.selector"
import { useAiPolicyMcpServers } from "@/pages/GovernancePage/applications/ai-policies/useLiveAiPolicies"
import {
  ACCESS_TYPES_RULE,
  getAiPolicyScope,
  getMcpToolOptions,
  getMcpToolWarning,
  hasConnectors,
  NETWORK_DESTINATION_RULE,
  WHOLE_WORKLOAD_RULE
} from "@/pages/GovernancePage/applications/ai-policies/useLiveAiPolicies.selector"
import { useAiPolicies } from "@/pages/GovernancePage/applications/policies/useAiPolicies"
import { getRuleOption } from "@/pages/GovernancePage/applications/policies/useAiPolicies.selector"
import { CardTemplate } from "@/pages/GovernancePage/components/pages/AiPolicyPage/components/CardTemplate"
import { RuleValueField } from "@/pages/GovernancePage/components/pages/AiPolicyPage/components/RuleField/RuleValueField"
import { DetectionField } from "./DetectionField"

import stylesRuleCard from "@/pages/GovernancePage/components/pages/AiPolicyPage/components/RuleCard/RuleCard.module.scss"
import styles from "@/pages/GovernancePage/components/pages/AiPolicyPage/components/RuleField/RuleField.module.scss"
import localStyles from "./LiveRuleField.module.scss"

import type { Rule } from "@/atoms"
import type { LiveRuleOption } from "@/pages/GovernancePage/applications/ai-policies/useLiveAiPolicies.types"
import type { RuleOptionGroup } from "@/pages/GovernancePage/applications/policies/getDemoData.types"
import type { AiPolicyFormRule } from "@/pages/GovernancePage/components/pages/AiPolicyPage/components/AiPolicyForm/AiPolicyForm.types"
import type { RuleFieldProps } from "@/pages/GovernancePage/components/pages/AiPolicyPage/components/RuleField/RuleField.types"

// Only a live option can have a detection
const getDetection = (option: object | undefined) =>
  (option as Partial<LiveRuleOption> | undefined)?.detection

const getScope = (option: object | undefined) =>
  (option as Partial<LiveRuleOption> | undefined)?.scope ?? getDetection(option)

const isSoleGroup = (group: RuleOptionGroup) =>
  group.options.some((option) => !!getDetection(option))

// The live rule card: deny rules only, no rule actions; a guardrail stands
// alone, and so does Workload Access with its destinations
export const LiveRuleField = (props: RuleFieldProps) => {
  const { name, type, listActions } = props
  const { t } = useTranslation()
  const { t: tForm } = useTranslation("translation", {
    keyPrefix: "governance.ai.form"
  })
  const { form } = Form.useForm()
  const { ruleOptions, live } = useAiPolicies()
  const isMcpServer = live?.kind === "mcpServer"
  // An MCP server policy's rules are on the server's tools and workload
  const ruleKey = isMcpServer
    ? "governance.ai.live.form.rule.mcpServer"
    : "governance.ai.live.form.rule"
  const rules = Form.useWatch([type, name, "rules"], form)
  const countByType = Form.useWatch(type, form)
  const isEmpty = !(rules ?? [])?.length
  const lastIndex = countByType?.length - 1
  const addNextConditionsList = () => listActions.add({ rules: [{}] })
  const isLastElement = name === lastIndex
  // Workload Access takes only its access types and destinations: an empty
  // row to add one only while it is missing
  const isWholeWorkload = (rules ?? []).some(isWholeWorkloadRow)
  const scopeValues = {
    resources: Form.useWatch("resources", form),
    specific_resources: Form.useWatch("specific_resources", form)
  }
  const { platforms = [] } = getAiPolicyScope(scopeValues)
  // An MCP server policy's tool names are offered from its servers' tools
  const { servers } = useAiPolicyMcpServers(isMcpServer)
  const mcpScope = getAiPolicyScope(scopeValues, "mcpServer")
  const mcpToolNamesOption =
    isMcpServer &&
    getMcpToolNamesOption(t, getMcpToolOptions(t, servers, mcpScope))
  const rulesFields = [{ required: true, message: t("error.requiredField") }]
  // A tool name the Kodem sensor would block nothing for warns, and saves
  const mcpToolRules: Rule[] = [
    ...rulesFields,
    {
      warningOnly: true,
      validator: async (_, toolNames?: string[]) => {
        const warning = getMcpToolWarning(t, toolNames ?? [], servers, mcpScope)

        if (warning) throw new Error(warning)
      }
    }
  ]
  // A sole condition takes no other: once a card has one, none is offered.
  // Connectors are offered only where the scope has them
  const options = (
    (rules ?? []).some(
      (rule: AiPolicyFormRule | undefined) => !!rule?.condition
    )
      ? ruleOptions.filter((group) => !isSoleGroup(group))
      : ruleOptions
  )
    .map((group) => ({
      ...group,
      options: group.options.filter(
        (option) =>
          (option.id !== "connectors" || hasConnectors(platforms)) &&
          isOfferedInCard(option.id, rules ?? [])
      )
    }))
    // A group with nothing left to offer shows no heading
    .filter((group) => group.options.length > 0)
  const cardRules: Rule[] = [
    {
      validator: async (_, value?: (AiPolicyFormRule | undefined)[]) => {
        if (isToolAccessWithoutAccess(value ?? []))
          throw new Error(
            t("governance.ai.live.form.rule.toolAccessWithoutAccess")
          )
        if (isWholeWorkloadWithoutDestinations(value ?? []))
          throw new Error(
            t("governance.ai.live.form.rule.wholeWorkloadWithoutDestinations")
          )
      }
    }
  ]
  const setRules = (next: (AiPolicyFormRule | undefined)[]) =>
    form.setFieldValue([type, name, "rules"], next)
  // Tool Access is no rule of its own: its tool name takes its place, with the
  // empty row its access types or destinations go in
  const addToolAccess = (index: number) =>
    setRules([
      ...(rules ?? []).slice(0, index),
      toolAccessRow(),
      ...(rules ?? [])
        .slice(index + 1)
        .filter((rule: AiPolicyFormRule | undefined) => !!rule?.condition),
      {}
    ])
  // Its access types and destinations go with its tool name; the card goes
  // when nothing else is left
  const removeToolAccess = () => {
    const left = (rules ?? []).filter(
      (rule: AiPolicyFormRule | undefined) =>
        !!rule?.condition && !isToolAccessPart(rule)
    )

    if (!left.length) return listActions.remove(name)
    setRules([...left, {}])
  }
  const scopeText = (option: object | undefined) =>
    ` ${tForm(`rules.scopes.${getScope(option)}`, "")}`

  return (
    <Flex vertical gap={16}>
      <CardTemplate type={type} variant={isEmpty ? "white" : undefined}>
        {isEmpty && (
          <Form.Item hidden name={[name, "rules"]} valuePropName="checked" />
        )}
        {isEmpty && (
          <Flex vertical gap={8}>
            <Text>{t(`${ruleKey}.empty`)}</Text>
            <Button
              type="link"
              size="small"
              className={styles.buttonP0}
              onClick={addNextConditionsList}
            >
              {t("governance.ai.live.form.rule.add")}
            </Button>
          </Flex>
        )}
        {!isEmpty && (
          <Flex vertical gap={10} flex={1}>
            <Text type="secondary" className="sm-text-ks">
              {t("governance.ai.live.form.rule.allMatch")}
            </Text>
            <Form.List name={[name, "rules"]}>
              {(fields, _actions) => {
                return (
                  <Flex vertical className={stylesRuleCard.wrapper}>
                    {fields.map(({ key, name: _name }, _index) => {
                      const currentCondition = rules[_name]?.["condition"]
                      const isMcpToolRow =
                        !!mcpToolNamesOption && isToolAccessRow(rules[_name])
                      const conditionOption =
                        isMcpToolRow && mcpToolNamesOption
                          ? mcpToolNamesOption
                          : getRuleOption(ruleOptions, currentCondition)
                      const detection = getDetection(conditionOption)
                      const addNextRule = () => {
                        // The form's own rows: typing outruns the watched
                        // ones, and a new row is undefined
                        const rows = form.getFieldValue([type, name, "rules"])
                        const isLast = rows.length === _name + 1

                        if (
                          isLast &&
                          (!isWholeWorkload ||
                            hasWholeWorkloadConditionToAdd(rows))
                        )
                          _actions.add()
                      }
                      const showAnd = _index !== 0 && currentCondition
                      const label = (
                        <Flex gap={6}>
                          <Text className={styles.label}>
                            {conditionOption?.displayName}
                          </Text>
                          {!!getScope(conditionOption) && (
                            <Text className={styles.label} type="secondary">
                              {scopeText(conditionOption)}
                            </Text>
                          )}
                        </Flex>
                      )
                      const isAccessTypesRow =
                        currentCondition === ACCESS_TYPES_RULE
                      // The whole workload's access types are network for
                      // now: required, the others disabled
                      const isWholeWorkloadAccessTypes =
                        isAccessTypesRow && isWholeWorkload && !!conditionOption
                      // Each destination has its own remove, in this column:
                      // the last one's removes the condition
                      const isDestinationsRow =
                        currentCondition === NETWORK_DESTINATION_RULE
                      // The whole workload requires its network access type
                      // and a destination: neither row has a remove, nor its
                      // last destination
                      const isRequiredRow =
                        isWholeWorkload &&
                        (isAccessTypesRow || isDestinationsRow)
                      const removeRow = () => {
                        if (!currentCondition) return _actions.remove(_name)
                        if (isToolAccessRow(rules[_name]))
                          return removeToolAccess()
                        // The card is the whole workload's
                        if (isWholeWorkloadRow(rules[_name]))
                          return listActions.remove(name)

                        const lastCondition = rules[rules.length - 1]?.condition
                        if (fields.length === 1) listActions.remove(name)
                        else if (fields.length === 2 && !lastCondition)
                          listActions.remove(name)
                        else {
                          _actions.remove(_name)

                          if (lastCondition) _actions.add({})
                        }
                      }

                      return (
                        <Flex
                          key={key}
                          flex={1}
                          gap={8}
                          align={showAnd ? "center" : "start"}
                        >
                          <Flex vertical flex={1} gap={24}>
                            {currentCondition && (
                              <>
                                {showAnd && (
                                  <span className={stylesRuleCard.and}>
                                    {t("governance.ai.live.form.rule.and")}
                                  </span>
                                )}
                                {detection ||
                                currentCondition === WHOLE_WORKLOAD_RULE ? (
                                  // A guardrail is detected, and the whole
                                  // workload is all of the agent: neither
                                  // takes a value
                                  <DetectionField
                                    name={_name}
                                    label={label}
                                    help={
                                      detection
                                        ? undefined
                                        : t(`${ruleKey}.wholeWorkloadHelp`)
                                    }
                                  />
                                ) : (
                                  <Flex flex={1} align="start" gap={12}>
                                    {label}
                                    {/* The destinations check their own condition */}
                                    {currentCondition !==
                                      NETWORK_DESTINATION_RULE && (
                                      <Form.Item
                                        hidden
                                        name={[_name, "condition"]}
                                      />
                                    )}
                                    <RuleValueField
                                      name={_name}
                                      option={
                                        isWholeWorkloadAccessTypes
                                          ? getWholeWorkloadAccessTypesOption(
                                              t,
                                              conditionOption
                                            )
                                          : conditionOption
                                      }
                                      // No access types is all of them, but
                                      // for the whole workload
                                      rules={
                                        isMcpToolRow
                                          ? mcpToolRules
                                          : isAccessTypesRow &&
                                              !isWholeWorkloadAccessTypes
                                            ? []
                                            : rulesFields
                                      }
                                      placeholder={
                                        isAccessTypesRow &&
                                        !isWholeWorkloadAccessTypes
                                          ? t(
                                              "governance.ai.live.form.enforcement.allAccessTypes"
                                            )
                                          : undefined
                                      }
                                      onChange={addNextRule}
                                      onRemove={
                                        isRequiredRow ? undefined : removeRow
                                      }
                                    />
                                  </Flex>
                                )}
                              </>
                            )}
                            {!currentCondition && (
                              <Form.Item
                                name={[_name, "condition"]}
                                className={styles.f1}
                              >
                                <Select
                                  // A short list: every condition is rendered
                                  virtual={false}
                                  // Left empty, the access types are all of
                                  // them, and a new destination row is there
                                  // to fill: the next condition can come now
                                  onSelect={(value: string) => {
                                    if (value === TOOL_ACCESS_RULE)
                                      return addToolAccess(_name)
                                    if (value === WHOLE_WORKLOAD_RULE)
                                      return setRules(wholeWorkloadRules())
                                    if (
                                      value === ACCESS_TYPES_RULE ||
                                      value === NETWORK_DESTINATION_RULE
                                    )
                                      addNextRule()
                                  }}
                                  classNames={{ popup: { list: styles.list } }}
                                  placeholder={t(
                                    "governance.ai.live.form.rule.addCondition"
                                  )}
                                  options={options}
                                  fieldNames={{
                                    label: "displayName",
                                    value: "id"
                                  }}
                                  optionRender={(option) => (
                                    <Flex align="center" gap={4}>
                                      <Text>{option.label}</Text>
                                      {"scope" in option.data && (
                                        <Text type="secondary">
                                          {scopeText(option.data)}
                                        </Text>
                                      )}
                                    </Flex>
                                  )}
                                />
                              </Form.Item>
                            )}
                          </Flex>
                          {isRequiredRow && !isDestinationsRow && (
                            <span className={styles.removeSpace} />
                          )}
                          {!isRequiredRow && !isDestinationsRow && (
                            <Button
                              type="text"
                              // The last empty row is where the next condition goes; any other empty row can go.
                              disabled={
                                !currentCondition &&
                                _index === fields.length - 1
                              }
                              icon={
                                <Text type="secondary" className="sm-text-ks">
                                  <CloseOutlined />
                                </Text>
                              }
                              key={`${showAnd}`}
                              className={cn(showAnd && styles.removeBtn)}
                              onClick={removeRow}
                            />
                          )}
                        </Flex>
                      )
                    })}
                  </Flex>
                )
              }}
            </Form.List>
            {/* The card's own error: Tool Access with nothing to enforce */}
            <Form.Item
              name={[name, "rules"]}
              rules={cardRules}
              className={localStyles.errorOnly}
            />
            <Flex flex={1} align="center" justify="end">
              <Button
                type="link"
                size="small"
                className={styles.buttonP0}
                onClick={() => listActions.remove(name)}
              >
                {t("governance.ai.live.form.rule.delete")}
              </Button>
            </Flex>
          </Flex>
        )}
      </CardTemplate>
      {name < lastIndex && (
        <Flex align="center" gap={12}>
          <Flex flex={1}>
            <Divider className={styles.divider14} />
          </Flex>
          <Text type="secondary" className={cn("sm-text-ks", styles.mono)}>
            {t("governance.ai.live.form.rule.or")}
          </Text>
          <Flex flex={1}>
            <Divider className={styles.divider14} />
          </Flex>
        </Flex>
      )}
      {isLastElement && rules?.length > 0 && (
        <span>
          <Button
            color="primary"
            variant="dashed"
            className={styles.buttonP0}
            onClick={addNextConditionsList}
          >
            {t("governance.ai.live.form.rule.anotherOr")}
          </Button>
        </span>
      )}
    </Flex>
  )
}
